import { useMutation } from '@apollo/client';
import { CarouselItemId, NavButtonType } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import { useSelector } from 'react-redux';

import {
  setCurrentCarouselItem,
  setCurrentCarouselItemToPrevious,
  setServiceAddress,
} from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { Phrase } from 'components/Phrase';
import { ThemedCircleButton } from 'components/ThemedCircleButton';
import { ReactComponent as EditSvg } from 'components/svg/Edit.svg';
import { ReactComponent as InputErrorSvg } from 'components/svg/InputError.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  ICarouselItemAddressNotFoundBottomProps,
  IState,
} from 'typings';
import {
  ErrorCode,
  IRegisterAddressMutation,
  IRegisterAddressMutationVariables,
} from 'typings/GraphQL';

import {
  Address,
  Buttons,
  Container,
  ContextBubbleStyled,
  Description,
  FormGroup,
  MutedContainer,
  Subhead,
  SubheadMuted,
  SubheadText,
} from './styles';

const registerAddressMutation = loader('../../queries/registerAddress.graphql');

export const CarouselItemAddressNotFoundBottom = ({
  bottomButtonType = NavButtonType.Default,
  destinationId = CarouselItemId.ScheduleEmergency,
  editAddressDestinationId = CarouselItemId.SalesAddress,
  parentCarouselItemId,
}: ICarouselItemAddressNotFoundBottomProps) => {
  const dispatch = useThunkDispatch();
  const {
    city = '',
    state = '',
    street1 = '',
    street2 = '',
    zipCode = '',
  } = useSelector(({ serviceCarousel, userInput }: IState) => ({
    city: userInput.address.city,
    currentItemId: serviceCarousel.currentItemId,
    state: userInput.address.state,
    street1: userInput.address.street1,
    street2: userInput.address.street2,
    zipCode: userInput.address.zipCode,
  }));

  const [registerAddress, { data, loading: isLoading }] = useMutation<
    IRegisterAddressMutation,
    IRegisterAddressMutationVariables
  >(registerAddressMutation, {
    onCompleted: (result: IRegisterAddressMutation) => {
      const { address, errorCode } = result.registerAddress || {};

      if (!errorCode && address) {
        dispatch(setServiceAddress(address));
        dispatch(
          setCurrentCarouselItem(CarouselReduxReducer.SERVICE, destinationId),
        );
      }
    },
  });

  const errorCode = data?.registerAddress?.errorCode;
  const isErrorVisible = errorCode === ErrorCode.ERROR_ADDRESS_INVALID;

  const handleEditClick = () => {
    dispatch(
      setCurrentCarouselItemToPrevious(
        CarouselReduxReducer.SERVICE,
        editAddressDestinationId,
      ),
    );
  };

  const handleContinueClick = async () => {
    registerAddress({
      variables: {
        address: {
          city,
          state,
          street1,
          street2,
          zipCode,
        },
      },
    });
  };

  return (
    <FormGroup>
      <Container>
        <Subhead>
          <InputErrorSvg />
          <SubheadText>
            <Phrase id="SALES_ADDRESS_NOT_FOUND.TITLE" />
          </SubheadText>
        </Subhead>
        <Description>
          <Phrase id="SALES_ADDRESS_NOT_FOUND.DESCRIPTION" />
        </Description>
        <MutedContainer>
          <SubheadMuted>
            <Phrase id="SALES_ADDRESS_NOT_FOUND.ADDRESS_TITLE" />
          </SubheadMuted>
          <Address>
            {street1} <br />
            {street2 && (
              <>
                {street2} <br />
              </>
            )}
            {`${city}, ${state} ${zipCode}`}
          </Address>
          <ThemedCircleButton
            icon={<EditSvg />}
            label={Phrase({ id: 'SALES_ADDRESS_NOT_FOUND.EDIT' })}
            onClick={handleEditClick}
          />
          <ContextBubbleStyled
            hasStreet2={!!street2}
            isEnabled={isErrorVisible}
            pointer={{
              horizontal: 'center',
              vertical: 'bottom',
            }}
          >
            <Phrase id="SALES_ADDRESS_NOT_FOUND.ERROR" />
          </ContextBubbleStyled>
        </MutedContainer>
      </Container>
      <Buttons>
        <CarouselBottomButton
          buttonFormat={bottomButtonType}
          carouselItemId={parentCarouselItemId}
          isLoading={isLoading}
          onContinueClick={handleContinueClick}
        />
      </Buttons>
    </FormGroup>
  );
};
